import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { notificationsApi } from '../Services/notificationsApi';
import { getLatestId, setLatestId, resetLatestId } from '../Services/notificationsCursor';
import { upsertNotifications, chunkIds } from '../Services/notificationsOrder';
import { safeOn, safeOff, getVideochatSocket } from '../Services/videochatSocket';

const BASE_POLL_MS = 60000; // intervalo base (respaldo; el socket va primero)
const ACTIVE_POLL_MS = 10000; // intervalo mientras el usuario está activo
const INACTIVITY_MS = 60000; // sin actividad tras este tiempo, se vuelve al intervalo base
const ACTIVITY_EVENTS = ['mousemove', 'click', 'touchstart', 'touchend', 'scroll'];

const getChatIdFromPath = (pathname) => {
  const match = pathname.match(/^\/(?:model|user|client|admin|assistant)\/chat\/(\d+)/);
  return match ? match[1] : null;
};

const isChatListPath = (pathname) => /^\/(?:model|user|client|admin|assistant)\/chat\/?$/.test(pathname);

const isFromChat = (notification, chatId) =>
  notification.category === 'chat' && notification.data?.chat_id?.toString() === chatId;

/**
 * Notificaciones no leídas del usuario (para el badge/campana).
 *
 * Tiempo real: escucha `notification-created` / `notification-viewed` sobre el
 * socket global (el mismo que identifica la presencia) y reconcilia lo perdido
 * al reconectar con el cursor `latest_id`/`since_id`. El polling adaptativo
 * (10s activo / 60s reposo / pausado oculto) queda como respaldo.
 * Las notificaciones del chat abierto (o de la lista de chats) se marcan vistas
 * solas, como antes.
 */
const useNotifications = ({ isAuth, token }) => {
  const [notifications, setNotifications] = useState([]);
  const location = useLocation();
  // Para leer la lista actual desde los manejadores sin volver a suscribirse
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;

  // Los intervalos y listeners leen el token vía ref para no re-suscribirse en cada render
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const fetchingRef = useRef(false);

  // ¿La notificación corresponde al chat abierto o a la lista de chats? En esos
  // casos el usuario ya la está viendo y se marca vista sola.
  const isBodyVisible = useCallback((notification) => {
    const currentChatId = getChatIdFromPath(window.location.pathname);
    return (
      isFromChat(notification, currentChatId) ||
      (isChatListPath(window.location.pathname) && notification.category === 'chat')
    );
  }, []);

  // Reemplaza por id (una fila agrupada llega con su mismo id y un count nuevo) y deja la
  // lista por fecha, no por id. Solo entran las no vistas.
  const mergeUnread = useCallback((incoming) => {
    const unread = incoming.filter((n) => !n.viewed);
    if (unread.length) setNotifications((prev) => upsertNotifications(prev, unread));
  }, []);

  // Marca varias en bloques de 50 (el máximo de notification_ids), en vez de una petición
  // por cada una.
  const markIdsAsViewed = useCallback(
    (ids) => Promise.allSettled(chunkIds(ids).map((chunk) => notificationsApi.markAsViewed(chunk))),
    []
  );

  const markAsViewed = useCallback(async (notification) => {
    // Optimista: si el backend falla, la notificación reaparece en el siguiente poll
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    try {
      await notificationsApi.markAsViewed(notification.id);
    } catch (error) {
      console.error('Error marking notification as viewed:', error.response?.data || error.message);
    }
  }, []);

  // Varias a la vez (lo que estaba a la vista al cerrar la campana, o las de un chat al
  // abrirlo): optimista y en una sola petición por cada 50.
  const markManyAsViewed = useCallback(
    async (list) => {
      const ids = (list || []).map((n) => n.id);
      if (!ids.length) return;
      const gone = new Set(ids);
      setNotifications((prev) => prev.filter((n) => !gone.has(n.id)));
      await markIdsAsViewed(ids);
    },
    [markIdsAsViewed]
  );

  const markAllAsViewed = useCallback(async () => {
    // Tope: la más nueva de las que el usuario tiene a la vista (la lista va por fecha).
    // Lo que llegue o suba al tope después del clic queda sin ver.
    const newest = notificationsRef.current[0];
    // Optimista: si el backend falla, reaparecen en el siguiente poll
    setNotifications([]);
    try {
      await notificationsApi.markAllAsViewed({ upToId: newest?.id });
    } catch (error) {
      console.error('Error marking all notifications as viewed:', error.response?.data || error.message);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (document.visibilityState !== 'visible') return;
    if (!tokenRef.current || fetchingRef.current) return;

    fetchingRef.current = true;
    try {
      const response = await notificationsApi.unread();
      let unread = response.data || [];
      if (response.latest_id) setLatestId(response.latest_id);

      // Las del chat abierto no se muestran: se marcan vistas directo
      const currentChatId = getChatIdFromPath(window.location.pathname);
      if (currentChatId) {
        const fromOpenChat = unread.filter((n) => isFromChat(n, currentChatId));
        unread = unread.filter((n) => !isFromChat(n, currentChatId));
        await markIdsAsViewed(fromOpenChat.map((n) => n.id));
      }

      // En la lista de chats el usuario ya ve los mensajes nuevos: el indicador sobra
      if (isChatListPath(window.location.pathname)) {
        const chatNotifs = unread.filter((n) => n.category === 'chat');
        unread = unread.filter((n) => n.category !== 'chat');
        await markIdsAsViewed(chatNotifs.map((n) => n.id));
      }

      setNotifications(unread);
    } catch (error) {
      // Error transitorio: se conserva la última lista conocida
      console.error('Error fetching notifications:', error.response?.data || error.message);
    } finally {
      fetchingRef.current = false;
    }
  }, [markIdsAsViewed]);

  // Reconcilia lo creado mientras el socket estuvo caído (id > latest_id).
  const reconcile = useCallback(async () => {
    if (!tokenRef.current) return;
    try {
      const response = await notificationsApi.unread(getLatestId());
      if (response.latest_id) setLatestId(response.latest_id);
      const incoming = (response.data || []).filter((n) => !n.viewed);
      const visible = incoming.filter((n) => isBodyVisible(n));
      if (visible.length) await markIdsAsViewed(visible.map((n) => n.id));
      const rest = incoming.filter((n) => !isBodyVisible(n));
      if (rest.length) mergeUnread(rest);
      // since_id solo devuelve ids MAYORES: una fila agrupada a la que se le sumó un evento
      // mientras el socket estuvo caído conserva su id y no vendría. Se piden también las
      // no vistas completas, y se reemplazan por id con su count al día.
      await fetchNotifications();
    } catch {
      // best-effort: el polling vuelve a intentarlo
    }
  }, [isBodyVisible, mergeUnread, markIdsAsViewed, fetchNotifications]);

  // Reset al cerrar sesión
  useEffect(() => {
    if (!isAuth || !token) {
      setNotifications([]);
      resetLatestId();
    }
  }, [isAuth, token]);

  // Polling adaptativo (respaldo)
  useEffect(() => {
    if (!isAuth || !token) return undefined;

    fetchNotifications();

    const baseInterval = setInterval(fetchNotifications, BASE_POLL_MS);

    let activeInterval = null;
    let inactivityTimer = null;

    const stopActivePolling = () => {
      clearInterval(activeInterval);
      activeInterval = null;
    };

    const handleActivity = () => {
      if (document.visibilityState !== 'visible') return;
      if (!activeInterval) activeInterval = setInterval(fetchNotifications, ACTIVE_POLL_MS);
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(stopActivePolling, INACTIVITY_MS);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') stopActivePolling();
      else fetchNotifications();
    };

    ACTIVITY_EVENTS.forEach((event) =>
      document.addEventListener(event, handleActivity, { passive: true })
    );
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(baseInterval);
      stopActivePolling();
      clearTimeout(inactivityTimer);
      ACTIVITY_EVENTS.forEach((event) => document.removeEventListener(event, handleActivity));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuth, token, fetchNotifications]);

  // Refetch al cambiar de ruta
  useEffect(() => {
    fetchNotifications();
  }, [location.pathname, fetchNotifications]);

  // Tiempo real por socket + reconciliación al (re)conectar
  useEffect(() => {
    if (!isAuth || !token) return undefined;

    const onCreated = (payload) => {
      const notification = payload?.notification;
      if (!notification) return;
      setLatestId(notification.id);
      if (isBodyVisible(notification)) {
        notificationsApi.markAsViewed(notification.id).catch(() => {});
        return;
      }
      if (notification.viewed) return;
      mergeUnread([notification]);
    };

    // `all` quita todas (o, con `up_to`, solo las de esa fecha o anteriores); `ids`, las indicadas.
    const onViewed = (payload = {}) => {
      setNotifications((prev) => {
        if (payload.all) return payload.up_to ? prev.filter((n) => (n.created_at || '') > payload.up_to) : [];
        const ids = new Set(payload.ids || []);
        return prev.filter((n) => !ids.has(n.id));
      });
    };

    const onConnect = () => reconcile();

    safeOn('notification-created', onCreated);
    safeOn('notification-viewed', onViewed);
    safeOn('connect', onConnect);

    if (getVideochatSocket()?.connected) reconcile();

    return () => {
      safeOff('notification-created', onCreated);
      safeOff('notification-viewed', onViewed);
      safeOff('connect', onConnect);
    };
  }, [isAuth, token, isBodyVisible, mergeUnread, reconcile]);

  // Al entrar a un chat se marcan vistas las pendientes de esa conversación
  useEffect(() => {
    const currentChatId = getChatIdFromPath(location.pathname);
    if (!currentChatId) return;
    markManyAsViewed(notifications.filter((n) => isFromChat(n, currentChatId)));
  }, [location.pathname, notifications, markManyAsViewed]);

  return {
    notifications,
    hasChatNotifications: notifications.some((n) => n.category === 'chat'),
    unreadCount: notifications.length,
    markAsViewed,
    markManyAsViewed,
    markAllAsViewed,
    refresh: fetchNotifications,
  };
};

export default useNotifications;
